import React, { useState, useEffect, useCallback } from 'react';
import { Wallet, Store, ArrowUpCircle, ArrowDownCircle, ChevronRight, ChevronDown, Tag, PackageOpen, Undo2, PencilLine, Calendar } from 'lucide-react';
import { FiltroLojaMes, filtroVazio } from './trocoFiltros';
import { Moeda, brl, VERMELHO } from './trocoFormato';
import { TrocoStatusBadge, TROCO_DESTINO, TROCO_TIPO, ehAjuste, rotuloTipo } from './trocoStatus';

// ============================================================================
// Painel de Saldos — saldo do troco por loja, com extrato (agrupar/desagrupar)
// ============================================================================
// Visão consolidada do numerário, em TRÊS NÍVEIS (reestruturado 2026-07-30):
//
//     Loja  →  Período (mm-aaaa)  →  Transações daquele período
//
// A linha da LOJA é sempre a conta CUMULATIVA:
//     Saldo Inicial (semente) + Recebidos − Depósitos = Saldo Atual
// A linha do PERÍODO fecha a própria conta, independente:
//     abertura (= fechamento do mês anterior) + recebidos − depósitos = saldo do mês
//
// ⚠️ O filtro global de mês SAIU da tela. Antes, o período era um filtro no topo
// e a loja abria direto nos dias — para percorrer o histórico era preciso trocar
// o filtro a cada mês. Com o período dentro da árvore, o filtro global viraria a
// mesma pergunta feita duas vezes.
//
// ⚠️ Só aparece mês COM movimento. Mês vazio no meio do histórico viraria um nó
// que só repete o saldo anterior, e quem procura movimento teria de abrir um por
// um para descobrir que não há nada.
//
// ⚠️ Períodos em ordem CRESCENTE (Janeiro → Dezembro). É como se lê um
// calendário e, mais importante, é o que torna a corrente do saldo visível: a
// abertura de cada mês é o saldo do mês da linha logo acima.
//
// O extrato (GET /api/troco/extrato/:loja) mostra só o que MOVE o saldo (troco a
// partir de Recebido; movimento só quando Confirmado) — a lista completa, com
// pendentes, é a aba Controle.
//
// O Gerente vê só a própria loja (recorte no backend, nas duas rotas).

// 'YYYY-MM-DD' → 'DD/MM/YYYY'
const dataBR = (iso) => (iso || '').split('-').reverse().join('/');

// 'YYYY-MM' → 'Julho/2026'. O nó de período é o que a pessoa lê primeiro ao
// abrir a loja, então vale o nome do mês por extenso em vez de '07/2026'.
const MESES = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
    'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];
const mesBR = (competencia) => {
    const [ano, mes] = String(competencia || '').split('-');
    return MESES[Number(mes) - 1] ? `${MESES[Number(mes) - 1]}/${ano}` : competencia;
};

const FinanceiroTrocoSaldos = () => {
    const token = localStorage.getItem('token') || localStorage.getItem('authToken');
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    // Só quem lança troco ajusta o teto — os demais (Diretoria, Auditoria) leem.
    // O backend também trava (PUT /limite é Financeiro; Admin tem bypass).
    // ⚠️ `Operacional` (28/09/2026): acesso total ao Financeiro (nível Admin).
    const podeEditarTeto = ['Admin', 'Financeiro', 'Operacional'].includes(user?.perfil || '');

    const [saldos, setSaldos] = useState([]);
    const [lojas, setLojas] = useState([]);
    // ⚠️ SÓ LOJA — o filtro de mês SAIU daqui em 2026-07-30 (demanda Julia).
    // O período virou NÍVEL DA ÁRVORE (Loja → mm-aaaa → transações), e um filtro
    // global de mês passaria a competir com ele: filtrar julho e depois abrir o
    // nó "julho" é a mesma pergunta feita duas vezes. As colunas do topo voltam a
    // ser CUMULATIVAS (semente + tudo = saldo atual); o recorte por mês agora se
    // faz abrindo o mês.
    const [filtro, setFiltro] = useState(filtroVazio);
    const [isCarregando, setIsCarregando] = useState(false);

    // Teto de saldo por loja — veio da aba "Saldo Inicial", removida em 2026-07-28.
    // Fica aqui porque é ao lado do saldo que ele regula que o ajuste faz sentido:
    // dá para ver quem estourou e corrigir na mesma linha.
    const [limitePadrao, setLimitePadrao] = useState(null);
    const [rascunhoTeto, setRascunhoTeto] = useState({});   // { LOJA: valorDigitado }
    const [salvandoTeto, setSalvandoTeto] = useState(null); // LOJA em gravação
    const [salvoTeto, setSalvoTeto] = useState({});         // feedback "salvo" por loja

    // Extrato: buscado sob demanda (só quando a loja é aberta) e guardado por loja.
    const [extratos, setExtratos] = useState({});        // { LOJA: { saldoInicial, meses } }
    const [lojasAbertas, setLojasAbertas] = useState([]); // nomes de loja expandidos
    const [mesesAbertos, setMesesAbertos] = useState([]); // chaves 'LOJA|YYYY-MM'
    const [buscandoExtrato, setBuscandoExtrato] = useState([]);

    // Lista de lojas para o filtro (uma vez).
    useEffect(() => {
        fetch('/api/troco/lojas', { headers: { Authorization: `Bearer ${token}` } })
            .then(r => r.json())
            .then(d => { if (Array.isArray(d)) setLojas(d); })
            .catch(e => console.error('Erro ao carregar lojas:', e));
    }, [token]);

    // Carrega os saldos CUMULATIVOS da rede (sem recorte de mês — ver a nota do
    // estado `filtro`). Roda uma vez; o detalhe por período vem do extrato.
    useEffect(() => {
        const carregar = async () => {
            setIsCarregando(true);
            try {
                const url = '/api/troco/saldos-lojas';
                const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
                const data = await res.json();
                if (data && Array.isArray(data.lojas)) setSaldos(data.lojas);
                if (data && data.limitePadrao !== undefined) setLimitePadrao(data.limitePadrao);
                setRascunhoTeto({});   // o que estava digitado e não salvo se perde junto
            } catch (e) {
                console.error('Erro ao carregar saldos:', e);
            } finally {
                setIsCarregando(false);
            }
        };
        carregar();
    }, [token]);

    // Salva o teto de UMA loja. Campo vazio devolve a loja ao padrão da rede
    // (o backend interpreta '' como "sem teto próprio") — por isso o vazio é um
    // valor legítimo aqui, não um erro de preenchimento.
    const salvarTeto = async (loja) => {
        const digitado = rascunhoTeto[loja];
        if (digitado === undefined) return;   // nada mudou nesta linha

        setSalvandoTeto(loja);
        try {
            const res = await fetch(`/api/troco/limite/${encodeURIComponent(loja)}`, {
                method: 'PUT',
                headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({ limite: digitado === '' ? null : parseFloat(digitado) }),
            });
            if (res.ok) {
                // Atualiza a linha na hora: o teto muda e o "excedido" é recalculado
                // com o saldo que já está na tela.
                setSaldos(prev => prev.map(s => s.LOJA !== loja ? s : {
                    ...s,
                    teto: digitado === '' ? (limitePadrao ?? s.teto) : parseFloat(digitado),
                    tetoProprio: digitado !== '',
                    excedido: parseFloat(s.saldo || 0) >= (digitado === '' ? (limitePadrao ?? s.teto) : parseFloat(digitado)),
                }));
                setRascunhoTeto(prev => { const p = { ...prev }; delete p[loja]; return p; });
                setSalvoTeto(prev => ({ ...prev, [loja]: true }));
                setTimeout(() => setSalvoTeto(prev => { const p = { ...prev }; delete p[loja]; return p; }), 2000);
            } else {
                const data = await res.json().catch(() => ({}));
                console.error('Erro ao salvar o teto:', data.error);
            }
        } catch (e) {
            console.error('Falha ao salvar o teto:', e);
        } finally {
            setSalvandoTeto(null);
        }
    };

    // Abre/fecha a loja. Na primeira abertura, busca o extrato do período filtrado.
    const alternarLoja = useCallback(async (loja) => {
        if (lojasAbertas.includes(loja)) {
            setLojasAbertas(prev => prev.filter(l => l !== loja));
            return;
        }
        setLojasAbertas(prev => [...prev, loja]);
        if (extratos[loja]) return;   // já em cache para este mês

        setBuscandoExtrato(prev => [...prev, loja]);
        try {
            // Traz o histórico INTEIRO da loja, quebrado por competência.
            const url = `/api/troco/extrato/${encodeURIComponent(loja)}`;
            const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
            const data = await res.json();
            if (res.ok) setExtratos(prev => ({ ...prev, [loja]: data }));
        } catch (e) {
            console.error('Erro ao carregar o extrato:', e);
        } finally {
            setBuscandoExtrato(prev => prev.filter(l => l !== loja));
        }
    }, [token, lojasAbertas, extratos]);

    const alternarMes = (loja, mes) => {
        const chave = `${loja}|${mes}`;
        setMesesAbertos(prev => prev.includes(chave) ? prev.filter(c => c !== chave) : [...prev, chave]);
    };

    // Filtro de loja em memória.
    const visiveis = saldos.filter(s => !filtro.loja || s.LOJA === filtro.loja);

    const totais = visiveis.reduce((acc, s) => ({
        saldoInicial: acc.saldoInicial + parseFloat(s.saldoInicial || 0),
        recebido: acc.recebido + parseFloat(s.recebido || 0),
        depositado: acc.depositado + parseFloat(s.depositado || 0),
        saldo: acc.saldo + parseFloat(s.saldo || 0),
    }), { saldoInicial: 0, recebido: 0, depositado: 0, saldo: 0 });

    // Sem filtro de mês, a linha da loja é sempre a conta CUMULATIVA:
    //     semente + tudo recebido − tudo depositado = saldo de hoje.
    // O recorte por período vive dentro da árvore, um nó por mês.
    const sufixoMes = '';
    const rotuloSaldo = 'Saldo Atual';
    const rotuloInicial = 'Saldo Inicial';

    const th = { padding: '14px 20px', borderBottom: '1px solid #e2e8f0', fontSize: '13px', color: '#64748b', textTransform: 'uppercase', whiteSpace: 'nowrap' };
    const thNum = { ...th, textAlign: 'right' };
    const td = { padding: '14px 20px', fontSize: '14px', color: '#0f172a' };
    const tdNum = { ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: '600' };
    // Saída de dinheiro: sinal de menos em VERMELHO (decisão Julia, 2026-07-16 —
    // antes era o roxo do status "Depositado", para o vermelho ficar reservado ao
    // saldo negativo). Hoje a leitura é a do extrato bancário: entrada em azul,
    // saída em vermelho, em TODAS as telas do Troco (Saldos e Controle). O saldo
    // negativo continua se destacando pelo card inteiro vermelho, não pela cor do
    // número. Mudar aqui? Mude junto no card e na coluna Valor Depósito do Controle.
    const saida = (v) => `− ${brl(Math.abs(parseFloat(v || 0)))}`;

    return (
        <div>
            {/* `semMes`: o período agora é nível da árvore, não filtro do topo. */}
            <FiltroLojaMes lojas={lojas} filtro={filtro} onChange={setFiltro} semMes />

            <div style={{ backgroundColor: '#fff', borderRadius: '16px', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                <div style={{ padding: '24px', borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div style={{ padding: '10px', backgroundColor: '#e0f2f1', borderRadius: '10px' }}>
                        <Wallet size={22} color="#16a39c" />
                    </div>
                    <div>
                        <h2 style={{ margin: 0, fontSize: '18px', fontWeight: '800', color: '#0f172a' }}>Saldos de Troco por Loja</h2>
                        <p style={{ margin: '2px 0 0 0', fontSize: '13px', color: '#64748b' }}>
                            Saldo atual = saldo inicial + trocos recebidos − depósitos confirmados.
                            {' '}Clique na loja para abrir o histórico <strong>por período</strong>, e no período para ver as transações.
                            {podeEditarTeto && ' O Teto limita o numerário parado na loja — vazio herda o padrão da rede.'}
                        </p>
                    </div>
                    {isCarregando && <span style={{ fontSize: '12px', color: '#64748b', marginLeft: 'auto' }}>Carregando...</span>}
                </div>

                <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '840px' }}>
                        <thead style={{ backgroundColor: '#f8fafc' }}>
                            <tr>
                                <th style={{ ...th, textAlign: 'left' }}>Loja</th>
                                <th style={thNum}>{rotuloInicial}</th>
                                <th style={thNum}>Recebidos{sufixoMes}</th>
                                <th style={thNum}>Depósitos{sufixoMes}</th>
                                <th style={thNum}>{rotuloSaldo}</th>
                                <th style={thNum}>Teto</th>
                            </tr>
                        </thead>
                        <tbody>
                            {visiveis.length === 0 && !isCarregando ? (
                                <tr><td colSpan="6" style={{ ...td, textAlign: 'center', color: '#64748b', padding: '32px' }}>Nenhuma loja encontrada.</td></tr>
                            ) : (
                                visiveis.map((s) => {
                                    const aberta = lojasAbertas.includes(s.LOJA);
                                    const extrato = extratos[s.LOJA];
                                    const buscando = buscandoExtrato.includes(s.LOJA);

                                    return (
                                        <React.Fragment key={s.LOJA}>
                                            {/* Nível 1 — a loja */}
                                            <tr
                                                onClick={() => alternarLoja(s.LOJA)}
                                                style={{ borderBottom: '1px solid #f1f5f9', cursor: 'pointer', backgroundColor: aberta ? '#f8fafc' : '#fff' }}
                                            >
                                                <td style={{ ...td, fontWeight: '700' }}>
                                                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                                                        {aberta ? <ChevronDown size={16} color="#16a39c" /> : <ChevronRight size={16} color="#94a3b8" />}
                                                        <Store size={16} color="#94a3b8" /> {s.LOJA}
                                                    </span>
                                                </td>
                                                <td style={{ ...tdNum }}><Moeda valor={s.saldoInicial} cor="#475569" /></td>
                                                <td style={tdNum}>
                                                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', justifyContent: 'flex-end', color: '#2563eb' }}>
                                                        <ArrowUpCircle size={13} /> {brl(s.recebido)}
                                                    </span>
                                                </td>
                                                <td style={tdNum}>
                                                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', justifyContent: 'flex-end', color: VERMELHO }}>
                                                        <ArrowDownCircle size={13} /> {saida(s.depositado)}
                                                    </span>
                                                </td>
                                                <td style={{ ...tdNum, fontSize: '15px', fontWeight: '800' }}>
                                                    <Moeda valor={s.saldo} cor="#0f766e" />
                                                </td>

                                                {/* TETO — quanto de numerário a loja pode ter parado. Acima
                                                    dele o ENVIO trava (a partir de 01/08/2026). Campo vazio
                                                    devolve a loja ao padrão da rede. O clique não pode
                                                    borbulhar: a linha inteira abre/fecha o extrato. */}
                                                <td style={tdNum} onClick={(e) => e.stopPropagation()}>
                                                    {podeEditarTeto ? (
                                                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', justifyContent: 'flex-end' }}>
                                                            {s.excedido && <span title="Saldo acima do teto — o envio de troco está travado">⚠️</span>}
                                                            <input
                                                                type="number"
                                                                step="0.01"
                                                                min="0"
                                                                value={rascunhoTeto[s.LOJA] !== undefined
                                                                    ? rascunhoTeto[s.LOJA]
                                                                    : (s.tetoProprio ? s.teto : '')}
                                                                placeholder={limitePadrao !== null ? brl(limitePadrao).replace('R$ ', '') : 'padrão'}
                                                                onChange={(ev) => setRascunhoTeto(prev => ({ ...prev, [s.LOJA]: ev.target.value }))}
                                                                onBlur={() => salvarTeto(s.LOJA)}
                                                                onKeyDown={(ev) => { if (ev.key === 'Enter') ev.target.blur(); }}
                                                                disabled={salvandoTeto === s.LOJA}
                                                                title={s.tetoProprio ? 'Teto próprio desta loja. Apague para voltar ao padrão da rede.' : 'Herda o padrão da rede. Digite um valor para dar teto próprio a esta loja.'}
                                                                style={{
                                                                    width: '110px', padding: '6px 8px', textAlign: 'right',
                                                                    borderRadius: '6px', fontSize: '13px', fontVariantNumeric: 'tabular-nums',
                                                                    border: `1px solid ${s.excedido ? VERMELHO : '#cbd5e1'}`,
                                                                    color: s.tetoProprio ? '#0f172a' : '#94a3b8',
                                                                    backgroundColor: salvandoTeto === s.LOJA ? '#f1f5f9' : '#fff',
                                                                }}
                                                            />
                                                            {salvoTeto[s.LOJA] && <span style={{ fontSize: '11px', color: '#16a34a', fontWeight: '700' }}>salvo</span>}
                                                        </span>
                                                    ) : (
                                                        // Sem permissão de edição: só o número, grafando quando é herdado.
                                                        <span style={{ color: s.excedido ? VERMELHO : '#64748b', fontWeight: '600' }}>
                                                            {s.excedido && '⚠️ '}{brl(s.teto)}
                                                            {!s.tetoProprio && <span style={{ fontSize: '11px', color: '#94a3b8', fontWeight: '400' }}> (padrão)</span>}
                                                        </span>
                                                    )}
                                                </td>
                                            </tr>

                                            {/* Nível 2 — os PERÍODOS (mm-aaaa) do histórico da loja.
                                                Cada mês fecha a própria conta: abertura (= fechamento
                                                do mês anterior) + recebidos − depósitos = saldo do mês.
                                                Só aparece mês COM movimento — mês vazio viraria um nó
                                                que só repete o saldo anterior. */}
                                            {aberta && buscando && (
                                                <tr><td colSpan="6" style={{ ...td, padding: '12px 20px 12px 56px', color: '#64748b', fontSize: '13px', backgroundColor: '#f8fafc' }}>Carregando extrato...</td></tr>
                                            )}
                                            {aberta && !buscando && extrato && (extrato.meses || []).length === 0 && (
                                                <tr><td colSpan="6" style={{ ...td, padding: '12px 20px 12px 56px', color: '#64748b', fontSize: '13px', backgroundColor: '#f8fafc' }}>
                                                    Esta loja ainda não teve movimento. O saldo é a semente do ledger.
                                                </td></tr>
                                            )}
                                            {aberta && !buscando && extrato && (extrato.meses || []).map((m) => {
                                                const mesAberto = mesesAbertos.includes(`${s.LOJA}|${m.mes}`);
                                                return (
                                                    <React.Fragment key={m.mes}>
                                                        <tr
                                                            onClick={() => alternarMes(s.LOJA, m.mes)}
                                                            style={{ borderBottom: '1px solid #f1f5f9', cursor: 'pointer', backgroundColor: '#f8fafc' }}
                                                        >
                                                            <td style={{ ...td, padding: '10px 20px 10px 44px', fontSize: '13px', color: '#334155', fontWeight: '700' }}>
                                                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                                                                    {mesAberto ? <ChevronDown size={14} color="#64748b" /> : <ChevronRight size={14} color="#cbd5e1" />}
                                                                    <Calendar size={13} color="#64748b" />
                                                                    {mesBR(m.mes)}
                                                                    <span style={{ color: '#94a3b8', fontWeight: '500' }}>
                                                                        ({m.lancamentos.length} transaç{m.lancamentos.length > 1 ? 'ões' : 'ão'})
                                                                    </span>
                                                                </span>
                                                            </td>
                                                            {/* A abertura do mês é o fechamento do anterior — é ela
                                                                que faz a linha do mês fechar sozinha. */}
                                                            <td style={{ ...tdNum, padding: '10px 20px', fontSize: '13px', color: '#475569' }}>
                                                                <Moeda valor={m.abertura} cor="#475569" />
                                                            </td>
                                                            <td style={{ ...tdNum, padding: '10px 20px', fontSize: '13px', color: '#2563eb' }}>
                                                                {m.recebido ? brl(m.recebido) : <span style={{ color: '#cbd5e1', fontWeight: '400' }}>—</span>}
                                                            </td>
                                                            <td style={{ ...tdNum, padding: '10px 20px', fontSize: '13px', color: VERMELHO }}>
                                                                {m.depositado ? saida(m.depositado) : <span style={{ color: '#cbd5e1', fontWeight: '400' }}>—</span>}
                                                            </td>
                                                            <td style={{ ...tdNum, padding: '10px 20px', fontSize: '13px' }}>
                                                                <Moeda valor={m.saldo} cor="#0f766e" />
                                                            </td>
                                                            <td style={tdNum}></td>
                                                        </tr>

                                                        {/* Nível 3 — as TRANSAÇÕES do mês, com a data de cada uma,
                                                            em ordem cronológica e com o saldo corrido. */}
                                                        {mesAberto && m.lancamentos.map((l) => {
                                                            // `tipo` diz a PERNA (entrada/saída); `classe` diz o que o
                                                            // lançamento é (Troco/Devolucao/Correcao).
                                                            const isTroco = l.tipo === 'troco';
                                                            const acerto = ehAjuste(l.classe);
                                                            // Depósito enviado ao escritório não tem banana: no lugar do
                                                            // documento, o extrato mostra o próprio destino.
                                                            const aoEscritorio = !isTroco && l.destino === TROCO_DESTINO.ESCRITORIO;
                                                            return (
                                                                <tr key={`${l.tipo}-${l.id}`} style={{ borderBottom: '1px solid #f1f5f9', backgroundColor: '#fff' }}>
                                                                    <td style={{ ...td, padding: '8px 20px 8px 72px', fontSize: '13px', color: '#475569' }}>
                                                                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                                                                            {/* A DATA vem na própria transação: como o dia deixou
                                                                                de ser nível da árvore, é aqui que ela aparece. */}
                                                                            <span style={{ color: '#94a3b8', fontVariantNumeric: 'tabular-nums', minWidth: '76px' }}>
                                                                                {dataBR(l.data)}
                                                                            </span>
                                                                            {acerto
                                                                                ? (l.classe === TROCO_TIPO.DEVOLUCAO
                                                                                    ? <Undo2 size={13} color="#c2410c" />
                                                                                    : <PencilLine size={13} color="#4338ca" />)
                                                                                : isTroco
                                                                                    ? <Tag size={13} color="#2563eb" />
                                                                                    : <PackageOpen size={13} color={VERMELHO} />}
                                                                            {/* Acerto se identifica pelo que é; troco e depósito, pelo documento. */}
                                                                            {acerto
                                                                                ? rotuloTipo(l.classe)
                                                                                : isTroco
                                                                                    ? `Troco · Lacre ${l.documento || '—'}`
                                                                                    : aoEscritorio
                                                                                        ? 'Depósito · Escritório'
                                                                                        : `Depósito · Banana ${l.documento || '—'}`}
                                                                            {acerto
                                                                                ? <span style={{ fontSize: '12px', color: '#94a3b8', fontStyle: 'italic' }}>{l.motivo}</span>
                                                                                : <TrocoStatusBadge status={l.status} />}
                                                                        </span>
                                                                    </td>
                                                                    <td style={{ ...tdNum, padding: '8px 20px', fontSize: '13px', color: '#cbd5e1', fontWeight: '400' }}>—</td>
                                                                    <td style={{ ...tdNum, padding: '8px 20px', fontSize: '13px', fontWeight: '600', color: '#2563eb' }}>
                                                                        {isTroco ? brl(l.valor) : <span style={{ color: '#cbd5e1', fontWeight: '400' }}>—</span>}
                                                                    </td>
                                                                    <td style={{ ...tdNum, padding: '8px 20px', fontSize: '13px', fontWeight: '600', color: VERMELHO }}>
                                                                        {isTroco ? <span style={{ color: '#cbd5e1', fontWeight: '400' }}>—</span> : saida(l.valor)}
                                                                    </td>
                                                                    <td style={{ ...tdNum, padding: '8px 20px', fontSize: '13px', fontWeight: '600' }}>
                                                                        <Moeda valor={l.saldo} cor="#64748b" />
                                                                    </td>
                                                                </tr>
                                                            );
                                                        })}
                                                    </React.Fragment>
                                                );
                                            })}
                                        </React.Fragment>
                                    );
                                })
                            )}
                        </tbody>
                        {visiveis.length > 0 && (
                            <tfoot>
                                <tr style={{ backgroundColor: '#f1f5f9', borderTop: '2px solid #cbd5e1' }}>
                                    <td style={{ ...td, fontWeight: '800', textTransform: 'uppercase', fontSize: '13px', color: '#334155' }}>Total{filtro.loja ? '' : ' da Rede'}</td>
                                    <td style={tdNum}><Moeda valor={totais.saldoInicial} cor="#475569" /></td>
                                    <td style={{ ...tdNum, color: '#2563eb' }}>{brl(totais.recebido)}</td>
                                    <td style={{ ...tdNum, color: VERMELHO }}>{saida(totais.depositado)}</td>
                                    <td style={{ ...tdNum, fontSize: '15px', fontWeight: '800' }}>
                                        <Moeda valor={totais.saldo} cor="#0f766e" />
                                    </td>
                                    {/* Somar tetos não significa nada (não é um teto da rede),
                                        então a coluna fica vazia no total — de propósito. */}
                                    <td style={tdNum}></td>
                                </tr>
                            </tfoot>
                        )}
                    </table>
                </div>
            </div>
        </div>
    );
};

export default FinanceiroTrocoSaldos;
